import { invariant } from '@seen/core/invariant'
import type { JsonValue } from '@seen/core/json'
import type { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import {
  cn,
  DotSeparator,
  TypographyPageTitle,
  TypographySubtle,
} from '@seen/ui'
import { type JSX, useContext } from 'react'
import { toast } from 'sonner'
import { ExternalTextLink } from '#src/components/core/ExternalTextLink.tsx'
import { Form, type FormProps } from '#src/components/core/form/Form.tsx'
import { useFieldEditListener } from '#src/components/validated-tool-form/shared/useFieldEditListener.ts'
import type { UseValidatedToolFormReturn } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import { ValidatedToolMoreDropdownMenu } from '#src/components/validated-tool-form/shared/ValidatedToolMoreDropdownMenu.tsx'
import type {
  ValidatedToolSetValue,
  ValidatedToolSuggestionControls,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import { ValidatedToolSuggestionHeader } from '#src/components/validated-tool-form/shared/ValidatedToolSuggestionHeader.tsx'
import { CurrentParticipantContext } from '#src/contexts/index.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import type { FormSchema } from '#src/hooks/useForm.ts'

export interface ValidatedToolInnerFormProps<
  TSchema extends FormSchema = any,
  TForm = UseValidatedToolFormReturn<TSchema>,
> extends Omit<FormProps, 'onSubmit'> {
  validatedTool: ValidatedTool
  form: TForm
  subtitle?: string
  onSubmit?: (
    response: ValidatedToolResponse,
    form: TForm
  ) => void | Promise<void>
  response?: ValidatedToolResponse
  /**
   * Called after the response is deleted via the more menu. Forwarded to
   * {@link ValidatedToolMoreDropdownMenu}; see its `onDelete` docs for why the
   * in-note editor context supplies it (to reset the widget) while the
   * standalone page omits it (to navigate back).
   */
  onDelete?: () => void
  /* Build the tool-header Accept all / Reject all / Undo all controls, given the
   * form's suggestion `setValue`. Lives here rather than in each of the twenty
   * forms because the row carries no instrument-specific knowledge. Undefined
   * (or an empty result) hides the row, which is every form with no staged
   * suggestion. */
  renderSuggestionControls?: (
    setValue: ValidatedToolSetValue
  ) => ValidatedToolSuggestionControls
  /* Absorbed, never rendered. The router hands every form the whole suggestion
   * prop set; a form with no option fields to target (COPM, ENN, MOCA, SLUMS,
   * TUG) has no reason to name them, and without this they would fall through
   * that form's `...delegated` onto the underlying `<form>` element as unknown
   * DOM attributes. Declaring them here consumes them instead. */
  renderFieldSuggestions?: unknown
  prefillValuesByLinkId?: unknown
  /* Called when the clinician changes a field's value themselves — never for a
   * value a suggestion decision, a prefill or a reset wrote. Lives here, beside
   * the one `form` every tool hands in, so no form has to wire it. */
  onFieldEdit?: (linkId: string, value: JsonValue | undefined) => void
}

export function ValidatedToolInnerForm({
  form,
  validatedTool,
  children,
  subtitle,
  className,
  onSubmit,
  response,
  onDelete,
  renderSuggestionControls,
  renderFieldSuggestions: _absorbedFieldSuggestions,
  prefillValuesByLinkId: _absorbedPrefill,
  onFieldEdit,
  ...delegated
}: ValidatedToolInnerFormProps): JSX.Element {
  const practitioner = useCurrentPractitioner()
  const participant = useContext(CurrentParticipantContext)

  useFieldEditListener(form.watch, onFieldEdit)
  const handleSubmit = form.handleSubmit(
    async (valuesByLinkId) => {
      try {
        invariant(participant, 'Participant is required')
        const response = ValidatedToolResponse.build(validatedTool, {
          valuesByLinkId,
        })
        response.author = practitioner.reference
        response.subjectPatient = participant.reference
        invariant(onSubmit, 'Form submitted without onSubmit handler')
        await onSubmit(response, form)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Unknown error')
        throw error
      }
    },
    (errors) => {
      const elements = Object.keys(errors)
        .map((name) => document.querySelector(`[name="${name}"]`))
        .filter((el): el is Element => el !== null)

      elements.sort((a, b) =>
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      )

      const first = elements[0]
      if (first) {
        const scrollTarget =
          first.closest('[role="radiogroup"]')?.parentElement ?? first
        scrollTarget?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  )

  return (
    <Form
      onSubmit={handleSubmit}
      className={cn('flex flex-col gap-8', className)}
      {...delegated}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <TypographyPageTitle>{validatedTool.name}</TypographyPageTitle>
          {!!response && (
            <ValidatedToolMoreDropdownMenu
              validatedTool={validatedTool}
              response={response}
              onDelete={onDelete}
            />
          )}
        </div>
        {(!!subtitle || !!validatedTool.pdfUrl) && (
          <TypographySubtle className="text-foreground-secondary">
            {subtitle}
            {!!subtitle && !!validatedTool.pdfUrl && <DotSeparator />}
            {!!validatedTool.pdfUrl && (
              <ExternalTextLink href={validatedTool.pdfUrl}>
                Reference
              </ExternalTextLink>
            )}
          </TypographySubtle>
        )}
      </div>
      <ValidatedToolSuggestionHeader
        controls={renderSuggestionControls?.(form.setSuggestionValue)}
      />
      {children}
    </Form>
  )
}
